import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api/client';
import {
    FaPlus,
    FaComments,
    FaExclamationCircle,
    FaBug,
    FaLightbulb,
    FaQuestionCircle,
    FaWrench,
    FaGraduationCap
} from 'react-icons/fa';
import './SupportTickets.css';

interface SupportTicket {
    id: number;
    title: string;
    description: string;
    status: string;
    priority: string;
    category: string;
    createdAt: string;
    updatedAt: string;
    messageCount?: number;
    billable?: boolean;
    consumedFreeQuota?: boolean;
}

export function SupportTickets() {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: 'GENERAL',
        priority: 'MEDIUM'
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadTickets();
    }, []);

    const loadTickets = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiFetch('/api/support/tickets');
            if (!res.ok) throw new Error('Errore caricamento ticket');
            const data = await res.json();
            setTickets(data);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTicket = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await apiFetch('/api/support/tickets', {
                method: 'POST',
                body: JSON.stringify(formData)
            });
            if (!res.ok) throw new Error('Errore creazione ticket');
            setShowCreateDialog(false);
            setFormData({ title: '', description: '', category: 'GENERAL', priority: 'MEDIUM' });
            loadTickets();
        } catch (err: any) {
            alert('Errore: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'OPEN': return '#3498db';
            case 'IN_PROGRESS': return '#f39c12';
            case 'WAITING_CUSTOMER': return '#9b59b6';
            case 'RESOLVED': return '#2ecc71';
            case 'CLOSED': return '#95a5a6';
            default: return '#95a5a6';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'OPEN': return 'Aperto';
            case 'IN_PROGRESS': return 'In Lavorazione';
            case 'WAITING_CUSTOMER': return 'In Attesa';
            case 'RESOLVED': return 'Risolto';
            case 'CLOSED': return 'Chiuso';
            default: return status;
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'LOW': return '#95a5a6';
            case 'MEDIUM': return '#3498db';
            case 'HIGH': return '#f39c12';
            case 'URGENT': return '#e74c3c';
            default: return '#95a5a6';
        }
    };

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'BUG': return <FaBug />;
            case 'FEATURE_REQUEST': return <FaLightbulb />;
            case 'QUESTION': return <FaQuestionCircle />;
            case 'CUSTOMIZATION': return <FaWrench />;
            case 'TRAINING': return <FaGraduationCap />;
            default: return <FaComments />;
        }
    };

    if (loading) {
        return (
            <div className="support-loading">
                <div className="spinner"></div>
                <p>Caricamento ticket...</p>
            </div>
        );
    }

    return (
        <div className="support-tickets-page">
            <div className="page-header">
                <div>
                    <h1>🎫 Supporto Tecnico</h1>
                    <p>Gestisci le tue richieste di assistenza</p>
                </div>
                <button className="btn-primary" onClick={() => setShowCreateDialog(true)}>
                    <FaPlus /> Nuovo Ticket
                </button>
            </div>

            {error && (
                <div className="alert alert-error">
                    <FaExclamationCircle /> {error}
                </div>
            )}

            {tickets.length === 0 ? (
                <div className="empty-state">
                    <FaComments size={64} color="#95a5a6" />
                    <h3>Nessun ticket aperto</h3>
                    <p>Crea il tuo primo ticket di supporto per ricevere assistenza</p>
                    <button className="btn-primary" onClick={() => setShowCreateDialog(true)}>
                        <FaPlus /> Crea Ticket
                    </button>
                </div>
            ) : (
                <div className="tickets-grid">
                    {tickets.map(ticket => (
                        <div
                            key={ticket.id}
                            className="ticket-card"
                            onClick={() => navigate(`/support/${ticket.id}`)}
                        >
                            <div className="ticket-header">
                                <div className="ticket-icon">
                                    {getCategoryIcon(ticket.category)}
                                </div>
                                <div className="ticket-badges">
                                    <span
                                        className="badge"
                                        style={{ backgroundColor: getStatusColor(ticket.status) }}
                                    >
                                        {getStatusLabel(ticket.status)}
                                    </span>
                                    <span
                                        className="badge"
                                        style={{ backgroundColor: getPriorityColor(ticket.priority) }}
                                    >
                                        {ticket.priority}
                                    </span>
                                    {ticket.consumedFreeQuota && (
                                        <span className="badge badge-free">Quota gratuita</span>
                                    )}
                                    {!ticket.consumedFreeQuota && ticket.billable && (
                                        <span className="badge badge-billable">Fatturabile</span>
                                    )}
                                </div>
                            </div>
                            <h3>{ticket.title}</h3>
                            <p className="ticket-description">{ticket.description}</p>
                            <div className="ticket-footer">
                                <span className="ticket-meta">
                                    <FaComments /> {ticket.messageCount || 0} messaggi
                                </span>
                                <span className="ticket-meta">
                                    {new Date(ticket.createdAt).toLocaleDateString('it-IT')}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showCreateDialog && (
                <div className="modal-overlay" onClick={() => setShowCreateDialog(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h2>Nuovo Ticket di Supporto</h2>
                        <form onSubmit={handleCreateTicket}>
                            <div className="form-group">
                                <label>Titolo *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                                    required
                                    placeholder="Descrivi il problema in poche parole"
                                />
                            </div>

                            <div className="form-group">
                                <label>Descrizione *</label>
                                <textarea
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    required
                                    rows={6}
                                    placeholder="Fornisci tutti i dettagli necessari per aiutarci a risolvere il problema"
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Categoria</label>
                                    <select
                                        value={formData.category}
                                        onChange={e => setFormData({ ...formData, category: e.target.value })}
                                    >
                                        <option value="BUG">Bug - Problema Tecnico</option>
                                        <option value="FEATURE_REQUEST">Richiesta Funzionalità</option>
                                        <option value="QUESTION">Domanda</option>
                                        <option value="CUSTOMIZATION">Personalizzazione</option>
                                        <option value="TRAINING">Formazione</option>
                                        <option value="GENERAL">Generale</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Priorità</label>
                                    <select
                                        value={formData.priority}
                                        onChange={e => setFormData({ ...formData, priority: e.target.value })}
                                    >
                                        <option value="LOW">Bassa</option>
                                        <option value="MEDIUM">Media</option>
                                        <option value="HIGH">Alta</option>
                                        <option value="URGENT">Urgente</option>
                                    </select>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => setShowCreateDialog(false)}
                                    disabled={submitting}
                                >
                                    Annulla
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Creazione...' : 'Crea Ticket'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
